import React from 'react';
import {
  ApiOutlined,
  BarcodeOutlined,
  FileTextOutlined,
  LinkOutlined,
  LockOutlined,
  QrcodeOutlined
} from '@ant-design/icons';
import { useQRCode } from 'next-qrcode';
import styled from 'styled-components';

import { useCustomApiList } from 'api/customApi';
import { useInventoryList } from 'api/inventories';
import { useMerchantList } from 'api/merchants';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { ConfigStepPanel, TC } from '../configStudio/kit/ConfigPanel';
import { OptionGrid, OptionTile } from '../configStudio/kit/OptionTile';
import { RadioGroup } from '../wizard/kit/controls';
import { SelectField, TextField } from '../wizard/kit/fields';
import { Form, Hint, Line, Words } from '../wizard/kit/layout';
import { PickedItem } from '../wizard/wizardModel';
import { wz, wzType } from '../wizard/wizardStyles';
import { rewardStepErrors } from './rewardAdapter';
import {
  API_CODES,
  ApiCode,
  ApiParam,
  chooseRewardType,
  CODE_SOURCES,
  CodeSource,
  Ecc,
  IRewardStepProps,
  REWARD_STEP_TOTAL,
  REWARD_TYPES,
  RewardType,
  RewardWizardState,
  Symbology
} from './rewardModel';

const TR = `${TC}.reward`;
const TS = `${TC}.reward.source`;

const TYPE_ICONS: Record<RewardType, React.ReactNode> = {
  text: <FileTextOutlined />,
  qr: <QrcodeOutlined />,
  barcode: <BarcodeOutlined />,
  url: <LinkOutlined />,
  api: <ApiOutlined />,
  pin: <LockOutlined />
};

/** 56 Step 1/5: six type tiles in three columns; a pick moves on (no Next drawn). */
export const RewardTypeStep: React.FC<IRewardStepProps> = ({ state, update, back }) => {
  const { t } = useMultiLanguageTranslation();
  return (
    <ConfigStepPanel
      title={t(`${TR}.title`)}
      step={1}
      total={REWARD_STEP_TOTAL}
      question={t(`${TR}.type.question`)}
      onNext={null}
      onBack={back}
      testId="orbit-reward-step-type"
    >
      <OptionGrid $columns={3}>
        {REWARD_TYPES.map(type => (
          <OptionTile
            key={type}
            size="reward"
            icon={TYPE_ICONS[type]}
            label={t(`${TR}.type.${type}`)}
            selected={state.type === type}
            onSelect={() => update(current => chooseRewardType(current, type))}
            testId={`orbit-reward-type-${type}`}
          />
        ))}
      </OptionGrid>
    </ConfigStepPanel>
  );
};

type Patch = (patch: Partial<RewardWizardState>) => void;

// 57: the source radios on the left, the value on the right.
const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  column-gap: 30px;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;

  > * + * {
    margin-top: 12px;
  }
`;

// 57 / 69 / 70: "Code Source (select one)" and the value label, Bold 12.5 in the primary ink.
const FieldLabel = styled.span`
  color: ${wz.text};
  font-weight: 700;
  ${wzType.hint}
`;

const Required = styled.span`
  margin-left: 4px;
  color: ${wz.danger};
`;

// The drawn sources run one per line.
const Sources = styled.div`
  [role='radiogroup'] {
    flex-direction: column;
    align-items: flex-start;
  }

  [role='radiogroup'] > * + * {
    margin-top: 12px;
    margin-left: 0;
  }
`;

// 69 / 70 "Preview": a white code box, then the title, the note and the value in the accent.
const Preview = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
  padding: 16px;
  border: ${wz.line} solid ${wz.border};
  border-radius: 12px;
  background: ${wz.field};

  > * + * {
    margin-left: 16px;
  }
`;

const CodeBox = styled.div`
  display: flex;
  flex: none;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 76px;
  min-height: 76px;
  padding: 6px;
  border-radius: 8px;
  background: #ffffff;
  color: #0a1020;
  font-size: 10px;
`;

const Bars = styled.div`
  display: flex;
  align-items: stretch;
  height: 48px;
  margin-bottom: 4px;
`;

const PreviewText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  > * + * {
    margin-top: 4px;
  }
`;

const PreviewTitle = styled.span`
  color: ${wz.text};
  font-weight: 700;
  ${wzType.label}
`;

const PreviewValue = styled.span`
  color: ${wz.accent};
  ${wzType.hint}
`;

const Option = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 18px;

  > * + * {
    margin-top: 8px;
  }
`;

const InventorySelect: React.FC<{
  value: PickedItem | null;
  onChange: (value: PickedItem) => void;
}> = ({ value, onChange }) => {
  const { t } = useMultiLanguageTranslation();
  const { data, isLoading } = useInventoryList({ size: 100 });
  return (
    <SelectField<number>
      label={t(`${TS}.selectInventory`)}
      placeholder={t(`${TS}.selectInventory`)}
      width="lg"
      showSearch
      loading={isLoading}
      value={value?.id ?? null}
      options={(data ?? []).map(item => ({ value: item.id, label: item.name }))}
      onChange={id => {
        const item = data?.find(inventory => inventory.id === id);
        if (item !== undefined) onChange({ id: item.id, name: item.name });
      }}
    />
  );
};

const QrCode: React.FC<{ value: string; level: Ecc }> = ({ value, level }) => {
  const { SVG } = useQRCode();
  return <SVG text={value} options={{ type: 'svg', level, margin: 0, width: 64 }} />;
};

// An illustration of the value's bars, not a symbology encoder (no barcode library ships here).
const BarcodeBars: React.FC<{ value: string }> = ({ value }) => (
  <>
    <Bars aria-hidden="true">
      {Array.from(value).map((char, index) => (
        <span
          key={`${char}-${index}`}
          style={{
            width: (char.charCodeAt(0) % 3) + 1,
            marginRight: (char.charCodeAt(0) % 2) + 1,
            background: '#0A1020'
          }}
        />
      ))}
    </Bars>
    <span>{value}</span>
  </>
);

/** 57 / 69 / 70: where the codes come from; QR and barcode add a preview and their option. */
const CodeSourceFields: React.FC<{ state: RewardWizardState; patch: Patch }> = ({
  state,
  patch
}) => {
  const { t } = useMultiLanguageTranslation();
  const type = state.type ?? 'text';
  const valueLabel = t(`${TS}.${type === 'text' ? 'textValue' : `${type}Value`}`);
  const required = t(`${TS}.required`);
  const value = state.source === 'fixed' ? state.codeValue.trim() : '';
  return (
    <>
      <Columns>
        <Stack>
          <FieldLabel>{t(`${TS}.label`)}</FieldLabel>
          <Sources>
            <RadioGroup<CodeSource>
              name="orbit-reward-source"
              label={t(`${TS}.label`)}
              value={state.source}
              options={CODE_SOURCES.map(source => ({
                value: source,
                label: t(`${TS}.${source === 'fixed' && type !== 'text' ? 'fixedValue' : source}`)
              }))}
              onChange={source => patch({ source })}
            />
          </Sources>
        </Stack>
        <Stack>
          {state.source === 'fixed' ? (
            <>
              <FieldLabel>
                {valueLabel}
                <Required>{required}</Required>
              </FieldLabel>
              <TextField
                label={`${valueLabel} ${required}`}
                width="lg"
                value={state.codeValue}
                onChange={codeValue => patch({ codeValue })}
              />
            </>
          ) : null}
          {state.source === 'inventory' ? (
            <InventorySelect value={state.inventory} onChange={inventory => patch({ inventory })} />
          ) : null}
          {state.source === 'generate' ? <Hint>{t(`${TS}.generateHint`)}</Hint> : null}
          {state.source === 'csv' ? <Hint>{t(`${TS}.csvHint`)}</Hint> : null}
        </Stack>
      </Columns>
      {type === 'qr' || type === 'barcode' ? (
        <>
          <Option>
            <FieldLabel>{t(`${TS}.preview`)}</FieldLabel>
            <Preview data-testid={`orbit-reward-${type}-preview`}>
              {value !== '' ? (
                <CodeBox>
                  {type === 'qr' ? (
                    <QrCode value={value} level={state.ecc} />
                  ) : (
                    <BarcodeBars value={value} />
                  )}
                </CodeBox>
              ) : null}
              <PreviewText>
                <PreviewTitle>
                  {t(`${TS}.${type === 'qr' ? 'qrPreview' : 'barcodePreview'}`)}
                </PreviewTitle>
                <Hint>{t(`${TS}.previewBody`)}</Hint>
                {value !== '' ? <PreviewValue>{value}</PreviewValue> : null}
              </PreviewText>
            </Preview>
          </Option>
          <Option>
            <Words $muted>{t(`${TS}.${type === 'qr' ? 'errorCorrection' : 'symbology'}`)}</Words>
            <Line style={{ padding: 0 }}>
              {type === 'qr' ? (
                <SelectField<Ecc>
                  label={t(`${TS}.errorCorrection`)}
                  width="lg"
                  value={state.ecc}
                  options={(['L', 'M', 'Q', 'H'] as Ecc[]).map(level => ({
                    value: level,
                    label: t(`${TS}.ecc.${level}`)
                  }))}
                  onChange={ecc => patch({ ecc })}
                />
              ) : (
                <SelectField<Symbology>
                  label={t(`${TS}.symbology`)}
                  width="lg"
                  value={state.symbology}
                  options={(['code128', 'ean13', 'code39'] as Symbology[]).map(symbology => ({
                    value: symbology,
                    label: t(`${TS}.symbologies.${symbology}`)
                  }))}
                  onChange={symbology => patch({ symbology })}
                />
              )}
              <Hint>{t(`${TS}.unsupported`)}</Hint>
            </Line>
          </Option>
        </>
      ) : null}
    </>
  );
};

/** 58: one URL for everyone, or one per user (no module key: kept apart for the backend). */
const UrlFields: React.FC<{ state: RewardWizardState; patch: Patch }> = ({ state, patch }) => {
  const { t } = useMultiLanguageTranslation();
  const TU = `${TR}.url`;
  return (
    <Columns>
      <Stack>
        <FieldLabel>{t(`${TU}.label`)}</FieldLabel>
        <RadioGroup<RewardWizardState['urlType']>
          name="orbit-reward-url-type"
          label={t(`${TU}.label`)}
          value={state.urlType}
          options={[
            { value: 'static', label: t(`${TU}.static`) },
            { value: 'unique', label: t(`${TU}.unique`) }
          ]}
          onChange={urlType => patch({ urlType })}
        />
        {state.urlType === 'unique' ? <Hint>{t(`${TU}.uniqueUnsupported`)}</Hint> : null}
      </Stack>
      <Stack>
        <Words>{t(`${TU}.value`)}</Words>
        <TextField
          label={t(`${TU}.value`)}
          placeholder="https://merchant.example/redeem"
          width="lg"
          value={state.url}
          onChange={url => patch({ url })}
        />
      </Stack>
    </Columns>
  );
};

const ParamTable = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  align-items: center;
`;

/** 59 / 60: the external API, its per-user code, and the body parameter mapping. */
const ApiFields: React.FC<{ state: RewardWizardState; patch: Patch }> = ({ state, patch }) => {
  const { t } = useMultiLanguageTranslation();
  const TA = `${TR}.api`;
  const { data, isLoading } = useCustomApiList({ size: 100 });
  const setParam = (index: number, change: Partial<ApiParam>) =>
    patch({
      params: state.params.map((param, at) => (at === index ? { ...param, ...change } : param))
    });
  return (
    <Form>
      <Line>
        <Words>{t(`${TA}.select`)}</Words>
        <SelectField<number>
          label={t(`${TA}.select`)}
          placeholder={t(`${TA}.selectPlaceholder`)}
          width="lg"
          showSearch
          loading={isLoading}
          value={state.api?.id ?? null}
          options={(data ?? []).map(api => ({ value: api.id, label: api.name }))}
          onChange={id => {
            const api = data?.find(item => item.id === id);
            if (api !== undefined) {
              patch({
                api: { id: api.id, name: api.name },
                // The module seeds the mapping from the API's own body keys (`RewardMechanics`).
                params: (api.body_params_keys ?? []).map(
                  (item: { id?: number; key?: string; mapping?: string }) => ({
                    id: item.id ?? null,
                    key: item.key ?? '',
                    value: item.mapping ?? '',
                    prefix: '',
                    suffix: ''
                  })
                ),
                queryParams: (api.query_params_keys ?? []).map(
                  (item: { id?: number; key?: string }) => ({
                    id: item.id ?? null,
                    key: item.key ?? ''
                  })
                )
              });
            }
          }}
        />
      </Line>
      <Line>
        <Words>{t(`${TA}.unique`)}</Words>
      </Line>
      <RadioGroup<ApiCode>
        name="orbit-reward-api-code"
        label={t(`${TA}.unique`)}
        value={state.apiCode}
        options={API_CODES.map(code => ({
          value: code,
          label: t(`${TA}.${code === 'none' ? 'no' : code}`)
        }))}
        onChange={apiCode => patch({ apiCode })}
      />
      {state.apiCode === 'none' ? <Hint>{t(`${TA}.noHint`)}</Hint> : null}
      {state.apiCode === 'inventory' ? (
        <Line>
          <InventorySelect value={state.inventory} onChange={inventory => patch({ inventory })} />
        </Line>
      ) : null}
      {state.params.length > 0 ? (
        <>
          <FieldLabel style={{ marginTop: 16 }}>{t(`${TA}.mapping`)}</FieldLabel>
          <ParamTable role="table" aria-label={t(`${TA}.mapping`)}>
            {(['key', 'value', 'prefix', 'suffix'] as const).map(column => (
              <Words key={column} $muted role="columnheader">
                {t(`${TA}.${column}`)}
              </Words>
            ))}
            {state.params.map((param, index) => (
              <React.Fragment key={param.key || index}>
                <Words role="cell">{param.key}</Words>
                {(['value', 'prefix', 'suffix'] as const).map(column => (
                  <TextField
                    key={column}
                    label={`${param.key} ${t(`${TA}.${column}`)}`}
                    width="md"
                    value={param[column]}
                    onChange={value => setParam(index, { [column]: value })}
                  />
                ))}
              </React.Fragment>
            ))}
          </ParamTable>
          <Hint as="p">{t(`${TA}.mappingNote`, { braces: '{{ }}' })}</Hint>
        </>
      ) : null}
    </Form>
  );
};

/** 61: the one merchant who redeems with their PIN. */
const PinFields: React.FC<{ state: RewardWizardState; patch: Patch }> = ({ state, patch }) => {
  const { t } = useMultiLanguageTranslation();
  const TP = `${TR}.pin`;
  const { data, isLoading } = useMerchantList({ size: 100 });
  return (
    <Form>
      <Line>
        <Words>{t(`${TP}.select`)}</Words>
        <SelectField<number>
          label={t(`${TP}.select`)}
          placeholder={t(`${TP}.placeholder`)}
          width="lg"
          showSearch
          loading={isLoading}
          value={state.merchant?.id ?? null}
          options={(data ?? []).map(merchant => ({ value: merchant.id, label: merchant.name }))}
          onChange={id => {
            const merchant = data?.find(item => item.id === id);
            if (merchant !== undefined)
              patch({ merchant: { id: merchant.id, name: merchant.name } });
          }}
        />
      </Line>
      <Hint>{t(`${TP}.hint`)}</Hint>
    </Form>
  );
};

const QUESTION: Record<RewardType, string> = {
  text: `${TS}.textQuestion`,
  qr: `${TS}.qrQuestion`,
  barcode: `${TS}.barcodeQuestion`,
  url: `${TR}.url.question`,
  api: `${TR}.api.question`,
  pin: `${TR}.pin.question`
};

/** Step 2/5: the type's code or redemption setup (57, 58, 59 / 60, 61, 69, 70). */
export const RewardSourceStep: React.FC<IRewardStepProps> = ({ state, update, next, back }) => {
  const { t } = useMultiLanguageTranslation();
  const type = state.type ?? 'text';
  const patch: Patch = change => update(current => ({ ...current, ...change }));
  const fields = {
    url: <UrlFields state={state} patch={patch} />,
    api: <ApiFields state={state} patch={patch} />,
    pin: <PinFields state={state} patch={patch} />
  } as Partial<Record<RewardType, React.ReactNode>>;
  return (
    <ConfigStepPanel
      title={t(`${TR}.title`)}
      step={2}
      total={REWARD_STEP_TOTAL}
      question={t(QUESTION[type])}
      errors={rewardStepErrors(state, t)}
      onNext={next}
      onBack={back}
      testId="orbit-reward-step-source"
    >
      {fields[type] ?? <CodeSourceFields state={state} patch={patch} />}
    </ConfigStepPanel>
  );
};
